import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, BookOpen, Compass, Ruler } from 'lucide-react';
const features = [{
  title: 'Pinboard',
  description: 'Collect and organize places with custom pins. Tag by terrain, season, or mood. Your personal atlas of discoveries.',
  icon: MapPin,
  imagePattern: 'radial-gradient(circle at 30% 30%, #7A9E7E 0%, transparent 40%), radial-gradient(circle at 70% 70%, #C4572A 0%, transparent 40%)'
}, {
  title: 'Fieldbook',
  description: 'A journal that lives on the map. Write notes anchored to coordinates. Attach sketches, photos, and weather data.',
  icon: BookOpen,
  imagePattern: 'repeating-linear-gradient(45deg, #1a3a2a 0, #1a3a2a 1px, transparent 0, transparent 50%)'
}, {
  title: 'Terrain Compass',
  description: 'See elevation, terrain type, and trail data overlaid on illustrated maps. Plan routes that respect the landscape.',
  icon: Compass,
  imagePattern: 'conic-gradient(from 0deg at 50% 50%, #F5F0E1 0deg, #D4A843 180deg, #F5F0E1 360deg)'
}, {
  title: 'Meridian Tools',
  description: 'Distance calculators, coordinate converters, and waypoint generators. Cartography made accessible.',
  icon: Ruler,
  imagePattern: 'linear-gradient(to right, #1B2838 1px, transparent 1px), linear-gradient(to bottom, #1B2838 1px, transparent 1px)'
}];
export function FeaturesSection() {
  return <section id="features" className="py-24 bg-parchment relative" data-id="element-10">
      <div className="absolute inset-0 bg-paper-texture opacity-50 pointer-events-none" data-id="element-11" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-12">
        <div className="text-center mb-20" data-id="element-13">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-forest mb-4" data-id="element-14">
            Your Explorer's Toolkit
          </h2>
          <div className="w-24 h-1 bg-terracotta mx-auto rounded-full" data-id="element-15" />
        </div>

        <div className="space-y-24" data-id="element-16">
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} viewport={{
          once: true,
          margin: '-100px'
        }} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12`} data-id="element-17">
              {/* Image/Visual Side */}
              <div className="w-full md:w-1/2" data-id="element-18">
                <div className="relative aspect-[4/3] bg-parchment-dark rounded-lg overflow-hidden border-2 border-forest/10 shadow-xl group" data-id="element-19">
                  {/* Decorative Pattern Background */}
                  <div className="absolute inset-0 opacity-20 transition-transform duration-700 group-hover:scale-105" style={{
                background: feature.imagePattern,
                backgroundSize: index === 1 ? '20px 20px' : index === 3 ? '40px 40px' : 'cover'
              }} data-id="element-20" />

                  {/* Overlay Topography */}
                  <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-21">
                    <path d={`M0,${20 + index * 10} Q50,${50 + index * 5} 100,${20 + index * 10}`} fill="none" stroke="#1a3a2a" strokeWidth="0.5" data-id="element-22" />
                    <path d={`M0,${40 + index * 10} Q50,${70 + index * 5} 100,${40 + index * 10}`} fill="none" stroke="#1a3a2a" strokeWidth="0.5" data-id="element-23" />
                  </svg>

                  {/* Centered Icon Visual */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-24">
                    <div className="w-24 h-24 bg-parchment rounded-full flex items-center justify-center shadow-lg border-4 border-double border-forest/20" data-id="element-25">
                      <feature.icon className="w-10 h-10 text-forest" data-id="element-26" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Side */}
              <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-27">
                <div className="inline-flex items-center justify-center md:justify-start space-x-3 mb-4" data-id="element-28">
                  <span className="text-terracotta font-serif font-bold text-xl" data-id="element-29">
                    0{index + 1}.
                  </span>
                  <h3 className="text-3xl font-serif font-bold text-forest" data-id="element-30">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-lg text-forest/80 font-sans leading-relaxed mb-6" data-id="element-31">
                  {feature.description}
                </p>
                <button className="text-forest font-bold border-b-2 border-terracotta/50 hover:border-terracotta transition-colors pb-1" data-id="element-32">
                  Learn more
                </button>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}